import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { api, ApiError, downloadOrderDocument, type OrderDocument, type VerifyResponse } from "@/lib/api";
import Logo from "@/pages/home/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { loadCheckoutDetails } from "@/pages/checkout/checkoutDetails";
import Footer from "@/pages/home/components/Footer";

type Status = "loading" | "success" | "failed" | "error";

/**
 * A key under `paymentResult.messages` plus its values, translated at render so
 * switching language re-renders it. `bank` is filled in from the provider.
 */
interface Message {
  key: string;
  values?: Record<string, string | number>;
}

export default function PaymentResultPage() {
  const { t } = useTranslation();
  const { user, ready, refreshProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<Message | null>(null);
  const handled = useRef(false);
  const [downloading, setDownloading] = useState<OrderDocument | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const payment = searchParams.get("payment");
  const orderId = searchParams.get("order");
  // The order's pay id. Lets a signed-out buyer confirm their own payment.
  const ref = searchParams.get("ref") ?? undefined;
  const provider = searchParams.get("provider") === "postbank" ? "postbank" : "dsk";
  const bankName = t(`common.banks.${provider}`);

  useEffect(() => {
    if (handled.current || !ready) return;

    handled.current = true;

    (async () => {
      // DSK failUrl — the customer cancelled or the payment was rejected.
      if (payment === "fail") {
        if (orderId) {
          await api.post("/payments/dsk/cancel", { orderId, ref }).catch(() => {});
        }
        setStatus("failed");
        setMessage({ key: "cancelled" });
        return;
      }

      if (payment === "error") {
        setStatus("error");
        setMessage({ key: "unmatched" });
        return;
      }

      if (payment !== "return" || !orderId) {
        setStatus("error");
        setMessage({ key: "noInfo" });
        return;
      }

      try {
        // The server confirms with the bank and grants the credits in one step,
        // so the balance is never set from the browser and a reload cannot
        // credit the account twice.
        // PostBank can still be deciding when the customer lands here, so the
        // status is re-read a few times before giving up. The server keeps
        // checking afterwards either way.
        let result = await api.post<VerifyResponse>(`/payments/${provider}/verify`, {
          orderId,
          ref,
        });

        // Re-read a couple of times, but never for an order the bank has no
        // record of: that answer will not change by asking again.
        for (let attempt = 0; result.pending && !result.notFound && attempt < 3; attempt += 1) {
          setMessage({ key: "stillConfirming" });
          await new Promise((resolve) => setTimeout(resolve, 4000));
          result = await api.post<VerifyResponse>(`/payments/${provider}/verify`, {
            orderId,
            ref,
          });
        }

        if (result.pending) {
          setStatus(result.notFound ? "failed" : "error");
          setMessage({ key: result.notFound ? "notFound" : "notConfirmedYet" });
          return;
        }

        if (!result.paid) {
          const code = result.orderStatus ?? result.actionCode;
          const withCode = code !== undefined ? "Code" : "";
          setStatus("failed");
          // The bank's own description is shown as received.
          setMessage(
            result.actionCodeDescription
              ? {
                  key: `declinedWithReason${withCode}`,
                  values: { reason: result.actionCodeDescription, ...(code !== undefined && { code }) },
                }
              : { key: `declined${withCode}`, values: code !== undefined ? { code } : undefined }
          );
          return;
        }

        if (user) await refreshProfile();

        setStatus("success");
        setMessage({
          key: !user
            ? "activatedGuest"
            : result.alreadyProcessed
            ? "alreadyConfirmed"
            : result.pendingCapture
              ? "authorised"
              : "activated",
        });
      } catch (err) {
        // The gateway could not be reached or answered oddly — the order is
        // left alone rather than cancelling a payment that may have gone
        // through.
        setStatus("error");
        // A server message is shown as received; `reason` otherwise falls back
        // to our own "Unexpected error." at render.
        setMessage({
          key: "verifyFailed",
          values: { orderId, ...(err instanceof ApiError && { reason: err.message }) },
        });
      }
    })();
  }, [ready, user, payment, orderId, ref, provider, refreshProfile]);

  const config: Record<Status, { icon: string; title: string; tone: string; ring: string }> = {
    loading: {
      icon: "ri-loader-4-line",
      title: t("paymentResult.titles.loading"),
      tone: "text-primary-600",
      ring: "bg-primary-100",
    },
    success: {
      icon: "ri-check-line",
      title: t("paymentResult.titles.success"),
      tone: "text-accent-900",
      ring: "bg-accent-100",
    },
    failed: {
      icon: "ri-close-line",
      title: t("paymentResult.titles.failed"),
      tone: "text-red-600",
      ring: "bg-red-100",
    },
    error: {
      icon: "ri-error-warning-line",
      title: t("paymentResult.titles.error"),
      tone: "text-red-600",
      ring: "bg-red-100",
    },
  };

  const download = async (kind: OrderDocument) => {
    if (!orderId) return;
    setDownloading(kind);
    setDownloadError(null);
    try {
      await downloadOrderDocument(Number(orderId), kind, ref);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : t("orders.documents.failed"));
    } finally {
      setDownloading(null);
    }
  };

  const c = config[status];
  const messageText = message
    ? t(`paymentResult.messages.${message.key}`, {
        bank: bankName,
        reason: t("common.errors.unexpected"),
        ...message.values,
      })
    : "";

  return (
    <div className="min-h-screen bg-background-50">
      <header className="border-b border-background-200/70 bg-background-50/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto h-16 px-4 md:px-6 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("common.actions.backToHome")}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 md:px-6 py-20 md:py-28">
        <div className="bg-background-100 border border-background-200/70 rounded-2xl p-10 text-center">
          <div
            className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${c.ring}`}
          >
            <i
              className={`${c.icon} text-3xl ${c.tone} ${
                status === "loading" ? "animate-spin" : ""
              }`}
            ></i>
          </div>

          <h1 className="mt-6 font-heading font-bold text-xl text-foreground-950">{c.title}</h1>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{messageText}</p>

          {status === "success" && orderId && (
            <div className="mt-8 rounded-xl border border-background-200/70 bg-background-50 p-4 text-left">
              <p className="text-sm font-semibold text-foreground-950">{t("paymentResult.receipt.title")}</p>
              <p className="mt-1 text-xs leading-relaxed text-foreground-600">{t("paymentResult.receipt.emailed")}</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {(["receipt", "invoice"] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => void download(kind)}
                    disabled={downloading !== null}
                    className="inline-flex items-center justify-center gap-1.5 rounded-md border border-background-300/60 bg-background-50 px-3 py-2.5 text-xs font-semibold text-foreground-900 hover:bg-background-200/50 disabled:opacity-60 transition-colors cursor-pointer"
                  >
                    <i
                      className={
                        downloading === kind
                          ? "ri-loader-4-line animate-spin"
                          : kind === "receipt"
                            ? "ri-receipt-line"
                            : "ri-file-list-3-line"
                      }
                    ></i>
                    {t(`orders.documents.download_${kind}`)}
                  </button>
                ))}
              </div>
              {downloadError && <p className="mt-2 text-xs text-red-600">{downloadError}</p>}
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3">
            {status === "success" && !user && (
              <Link
                to="/auth"
                state={{
                  mode: "signup",
                  from: "/profile",
                  email: loadCheckoutDetails()?.customer.email,
                }}
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] cursor-pointer"
              >
                <i className="ri-user-add-line text-lg"></i>
                {t("paymentResult.createAccount")}
              </Link>
            )}

            {status === "success" && user && (
              <Link
                to="/profile"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] cursor-pointer"
              >
                <i className="ri-user-line text-lg"></i>
                {t("paymentResult.goToProfile")}
              </Link>
            )}

            {status === "failed" && (
              <Link
                to="/pricing"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line text-lg"></i>
                {t("common.actions.tryAgain")}
              </Link>
            )}

            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md border border-background-300/60 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-200/50 transition-colors cursor-pointer"
            >
              {t("common.actions.backToHome")}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
